import React from 'react';
import { Helmet } from 'react-helmet';

import DynamicHeadSegments from 'next/head';

import { LinkPresentational, TitleAndBodyBlockPresentational } from '../../types/common';
import { serverUtils } from '../../utils/serverUtils/serverUtils';

export type FaqStructureData = {
  '@context': string;
  '@type': string;
  mainEntity: {
    '@type': string;
    acceptedAnswer: {
      '@type': string;
      text: string;
    };
    name: string;
  }[];
};

export type BreadCrumbsStructureData = {
  '@context': string;
  '@type': string;
  itemListElement: {
    '@type': string;
    item: string;
    name: string;
    position: number;
  }[];
};

type OrganizationAddressStructuredData = {
  '@type': string;
  addressCountry: {
      '@type': string;
      name: string;
    };
  addressLocality: string;
    postalCode: string;
    streetAddress: string;
};

export type OrganizationStructuredDataItem = {
  address: OrganizationAddressStructuredData;
  contactPoint: {
    '@type': string;
    contactType: string;
    email: string;
    telephone?: string;
  };
  description: string;
  logo: string;
  name: string;
  openingHours?: string[];
  url: string;
};

export type OrganizationStructuredData = {
  '@context': string;
  '@type': string;
} & OrganizationStructuredDataItem;

export type UseGetStructuredDataParams = {
  breadcrumbs?: LinkPresentational[] | null;
  faqs?: TitleAndBodyBlockPresentational[] | null;
  organizationData?: OrganizationStructuredDataItem | null;
};

export const hooks = {
  useGetStructuredData({
    breadcrumbs,
    faqs,
    organizationData,
  }: UseGetStructuredDataParams):
    (FaqStructureData | BreadCrumbsStructureData | OrganizationStructuredData)[] | null {
    return React.useMemo(() => {
      if (!faqs && !breadcrumbs && !organizationData) {
        return null;
      }
      const structuredData = [];

      if (faqs && faqs.length > 0) {
        const faqData: FaqStructureData = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(item => ({
            '@type': 'Question',
            name: item.title,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.body.replace(/(<([^>]+)>)/gi, ''),
            },
          })),
        };
        structuredData.push(faqData);
      }

      if (breadcrumbs) {
        const breadCrumbsData: BreadCrumbsStructureData = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.title,
            item: CONFIG.basePath + item.url.href,
          })),
        };
        structuredData.push(breadCrumbsData);
      }

      if (organizationData) {
        const organizationStructuredData: OrganizationStructuredData = {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          ...organizationData,
        };

        structuredData.push(organizationStructuredData);
      }

      return structuredData;
    }, [faqs, breadcrumbs, organizationData]);
  },
};

export type ListItem = {
  item: string;
  name: string;
  position: number;
};

export type Props = {
  breadCrumbItems?: LinkPresentational[] | null;
  faqItems?: TitleAndBodyBlockPresentational[] | null;
  organizationData?: OrganizationStructuredDataItem | null;
};

export const StructuredDataContainer = React.memo((props: Props) => {
  const { faqItems, breadCrumbItems, organizationData } = props;
  const structuredData = hooks.useGetStructuredData({
    breadcrumbs: breadCrumbItems,
    faqs: faqItems,
    organizationData: organizationData,
  });

  const isNextBuild = serverUtils.isNextBuild();

  return structuredData && structuredData.length > 0 ? (
    isNextBuild
      ? <DynamicHeadSegments>
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </DynamicHeadSegments>
      : <Helmet>
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>
  ) : null;
});

StructuredDataContainer.displayName = 'StructuredDataContainer';
